import { fetchAuthSession } from 'aws-amplify/auth';
import { camelizeKeys } from 'humps';
import { IMPERSONATOR_TOKEN, POLICY_TYPES } from '@whitelabel/helpers/constants';
import { captureExceptionWithRecording, isOnServer } from '@whitelabel/helpers/utils';
import { isJSON } from '@whitelabel/helpers/objects';
import { getUserCountry } from '@whitelabel/helpers/site';
import { IError, IAPIHelper, APIProps } from './types';
import { ERROR_CODE } from './constants';

export const [
  API_HOST,
  CMS_API_HOST,
  XPAY_API_HOST,
  XPAY_CHARGE_PROVIDER_KEY,
  XPAY_CHARGE_BUNDLE_URL,
  XPAY_PAYOUT_BUNDLE_URL,
  GOOGLE_PLACES_API_KEY,
  LOCALE_HOST,
  ZENDESK_API_URL,
  DD_APPLICATION_ID,
  DD_CLIENT_TOKEN,
  APPLICATION_ENV,
] = [
  process.env.REACT_APP_API_HOST || process.env.NEXT_PUBLIC_API_HOST,
  process.env.REACT_APP_CMS_API_HOST || process.env.NEXT_PUBLIC_CMS_API_HOST,
  process.env.REACT_APP_XPAY_API_HOST || process.env.NEXT_PUBLIC_XPAY_API_HOST,
  process.env.REACT_APP_XPAY_CHARGE_PROVIDER_KEY || process.env.NEXT_PUBLIC_XPAY_CHARGE_PROVIDER_KEY,
  process.env.REACT_APP_XPAY_CHARGE_BUNDLE_URL || process.env.NEXT_PUBLIC_XPAY_CHARGE_BUNDLE_URL,
  process.env.REACT_APP_XPAY_PAYOUT_BUNDLE_URL || process.env.NEXT_PUBLIC_XPAY_PAYOUT_BUNDLE_URL,
  process.env.REACT_APP_GOOGLE_PLACES_API_KEY || process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY,
  process.env.REACT_APP_MESSAGES_HOST || process.env.NEXT_PUBLIC_MESSAGES_HOST || '',
  process.env.REACT_APP_ZENDESK_API_URL || process.env.NEXT_PUBLIC_ZENDESK_API_URL,
  process.env.REACT_APP_DD_APPLICATION_ID || process.env.NEXT_PUBLIC_DD_APPLICATION_ID,
  process.env.REACT_APP_DD_CLIENT_TOKEN || process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
  process.env.REACT_APP_ENV || process.env.NEXT_PUBLIC_ENV,
];

const AUTH_ACCESS_TOKEN_KEY = 'auth_access_token';
const AUTH_ID_TOKEN_KEY = 'auth_id_token';
const AUTH_REFRESH_TOKEN_KEY = 'auth_refresh_token';
const AUTH_USER_KEY = 'auth_user';

// The post-login action namespaces these, so they do not collide with the
// Cognito names below. Keep in sync with REGION_ID_COGNITO_TOKEN_FIELD_MAPPINGS
// in multiRegion.ts, which is not imported here to avoid a cycle.
const AUTH0_CUSTOMER_ID_CLAIMS: Record<string, string> = {
  'eu-central-1': 'xcover/cid_euce1',
  'us-east-1': 'xcover/cid_usea1',
  'ap-south-1': 'xcover/cid_apso1',
};

function decodeJwtPayload(token: string): any {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64));
}

export function isAuth0Token(token: string): boolean {
  try {
    const decoded = decodeJwtPayload(token);
    // auth0 sub will always be in this format <provider>|<id>
    // and cognito sub will always just be an uuid
    return typeof decoded.sub === 'string' && decoded.sub.includes('|');
  } catch {
    return false;
  }
}

function cognitoAuthUser(payload: any) {
  return {
    username: payload['cognito:username'] || payload.sub,
    email: payload.email,
    email_verified: payload.email_verified,
    preferred_username: payload.preferred_username,
    'custom:cid_euce1': payload['custom:cid_euce1'],
    'custom:cid_usea1': payload['custom:cid_usea1'],
    'custom:cid_apso1': payload['custom:cid_apso1'],
  };
}

// Auth0's sub is an opaque "auth0|..." string, so the username has to come from
// the customer ID for the token's own region. Claims are remapped onto the
// Cognito key names the rest of the app already reads.
function auth0AuthUser(payload: any) {
  const region = payload['xcover/region'];
  const username = payload[AUTH0_CUSTOMER_ID_CLAIMS[region]];
  if (!username) {
    throw new Error(`Auth0 ID token carries no customer ID for region ${region}`);
  }

  return {
    username,
    email: payload.email,
    email_verified: payload.email_verified,
    preferred_username: payload.preferred_username,
    'custom:cid_euce1': payload['xcover/cid_euce1'],
    'custom:cid_usea1': payload['xcover/cid_usea1'],
    'custom:cid_apso1': payload['xcover/cid_apso1'],
  };
}

export function storeAuthTokens(response: { accessToken: string; idToken: string; refreshToken?: string }) {
  const payload = decodeJwtPayload(response.idToken);
  const user = isAuth0Token(response.idToken) ? auth0AuthUser(payload) : cognitoAuthUser(payload);

  localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, response.accessToken);
  localStorage.setItem(AUTH_ID_TOKEN_KEY, response.idToken);
  if (response.refreshToken) {
    localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, response.refreshToken);
  }
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
}

export function getStoredAuthUser(): { username: string; [key: string]: any } | null {
  const stored = localStorage.getItem(AUTH_USER_KEY);
  if (!stored) return null;
  return JSON.parse(stored);
}

export function clearAuthTokens() {
  localStorage.removeItem(AUTH_ACCESS_TOKEN_KEY);
  localStorage.removeItem(AUTH_ID_TOKEN_KEY);
  localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = decodeJwtPayload(token);
    // 30 second buffer to avoid using a token that's about to expire
    return payload.exp * 1000 <= Date.now() + 30000;
  } catch {
    return true;
  }
}

let refreshPromise: Promise<string> | null = null;

export async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    clearAuthTokens();
    throw new Error('No refresh token');
  }

  // Refresh tokens are opaque in both providers, so the issuer has to come off
  // the ID token. Only iss is read, so its expiry does not matter here.
  const idToken = localStorage.getItem(AUTH_ID_TOKEN_KEY);
  const provider = idToken && isAuth0Token(idToken) ? 'auth0' : 'cognito';

  try {
    const response = await fetch(`${API_HOST}/v1/customers/token_refresh/${provider}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      throw new Error('Refresh token expired');
    }

    const data = await response.json();
    localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, data.access_token);
    localStorage.setItem(AUTH_ID_TOKEN_KEY, data.id_token);
    if (data.refresh_token) {
      localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, data.refresh_token);
    }
    return data.access_token;
  } catch (err) {
    clearAuthTokens();
    if (!isOnServer) window.location.reload();
    throw err;
  } finally {
    refreshPromise = null;
  }
}

export const getAccessToken = (auth: any, SSRAuth?: any) => {
  if (auth && typeof auth !== 'string') {
    const storedToken = localStorage.getItem(AUTH_ACCESS_TOKEN_KEY);
    if (storedToken) {
      if (!isTokenExpired(storedToken)) {
        return Promise.resolve(storedToken);
      }
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken();
      }
      return refreshPromise;
    }
    // LWA users still on Amplify session
    const currentSessionPromise = SSRAuth ? SSRAuth.currentSession() : fetchAuthSession();
    return currentSessionPromise
      .then(
        (currentSession: any) =>
          currentSession.accessToken?.jwtToken || currentSession.tokens?.accessToken?.toString(),
      )
      .catch((err: any) => {
        if (err?.name !== 'UserUnAuthenticatedException') captureExceptionWithRecording(err);
        if ((err === 'No current user' || err.message === 'Refresh Token has expired') && !isOnServer) {
          window.location.reload();
        } else throw err;
      });
  }
  return auth;
};

export const createHeaders = async (
  auth = false,
  headers: any,
  clientTimezone = false,
  impersonatorToken: string | null,
  SSRAuth?: any,
): Promise<Headers> =>
  new Headers({
    'Content-Type': 'application/json',
    ...(clientTimezone && { 'X-CLIENT-TIMEZONE': Intl.DateTimeFormat().resolvedOptions().timeZone }),
    ...(auth &&
      (impersonatorToken
        ? { Authorization: `Impersonator ${impersonatorToken}` }
        : { Authorization: `Bearer ${await getAccessToken(auth, SSRAuth)}` })),
    ...headers,
  });

export function processKeys(data: object) {
  return camelizeKeys(data, (key: any, convert: any, options: any) => {
    if (
      POLICY_TYPES.includes(key) ||
      ['_non_field_errors', '_error'].includes(key) ||
      key.includes('sectionFields') ||
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(key)
    )
      return key;
    return convert(key, options);
  }) as unknown as any;
}

export const ignoreErrors = (pathname: string, error: IError) => {
  const errors404 =
    (pathname.endsWith('/customers/email/') ||
      pathname.includes('/staticpages/') ||
      pathname.endsWith('/customers/forgot_password_initiate/') ||
      pathname.endsWith('/customers/lookup/') ||
      pathname.endsWith('/validate_token/') ||
      /\/messages\/partners\//.test(pathname)) &&
    error.status === 404;

  const errors422 =
    (pathname.endsWith('/validate_token/') || /\/customers\/[^/]+\/email\/$/.test(pathname)) && error.status === 422;

  const errors403 = pathname.startsWith('/api/v1/payout/') && error.status === 403;

  const otherErrors =
    // Please check https://domainpartners.atlassian.net/browse/XCOM-3577 for the APIs return the ALREADY_REGISTERED code.
    error.code === ERROR_CODE.ALREADY_REGISTERED ||
    (pathname.endsWith('/gdpr/') && error.errors?.code === ERROR_CODE.BOOKING_GDPR_CUSTOMER_NOT_EXISTED) ||
    pathname.endsWith('/api/v1/geoip/');

  return errors404 || errors422 || errors403 || otherErrors;
};

function captureError(error: any, url = '', requestId?: string | null) {
  if (url) {
    const pathname = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];
    if (ignoreErrors(pathname, error)) {
      return;
    }
  }
  captureExceptionWithRecording(error, {
    error: JSON.stringify(error),
    url,
    ...(requestId && { 'x-request-id': requestId }),
  });
}
export const createError = (status: any, response: any, url: any, requestId?: string | null) => {
  let errorMessage = '';
  const isJSONFormat = isJSON(response);
  errorMessage = response?.message || response?.detail || response;
  if (!isJSONFormat || typeof errorMessage !== 'string') {
    errorMessage = 'Sorry! Something went wrong. Please try again later.';
  }
  const allowedAttributes = ['requestId', 'type', 'code', 'href', 'errors', 'quotes', 'extra'];
  const errorAttributes = response
    ? Object.keys(response).filter((attribute) => allowedAttributes.includes(attribute))
    : [];
  const error: IError = new Error(errorMessage);

  error.status = status;
  error.isJSON = isJSONFormat;
  if (response?.errors) {
    error.errors = response.errors;
  }
  if (response?.code) {
    error.code = response.code;
  }
  if (requestId) {
    error.requestId = requestId;
  }
  captureError(error, url, requestId);

  const updatedError = error as unknown as Record<string, unknown>;

  errorAttributes.reduce((accumulator, attribute) => {
    if (attribute === 'type') {
      accumulator.name = response[attribute];
    } else {
      accumulator[attribute] = response[attribute];
    }

    return accumulator;
  }, updatedError);

  return updatedError;
};

export const processResponse = async (response: any, skipCamelization = false) => {
  let responseText;
  try {
    responseText = await response.text();
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const responseJSON = JSON.parse(responseText);
      // log for nextjs server response
      if (isOnServer) {
        console[response.ok ? 'log' : 'error']({
          url: response.url,
          status: response.status,
          status_text: response.statusText,
          response_json: JSON.stringify(responseJSON),
        });
      }

      // quote-and-bind package has specific needs for api mappings
      if (skipCamelization) {
        return responseJSON;
      }

      const data = camelizeKeys(responseJSON, (key: any, convert: any, options: any) => {
        if (
          POLICY_TYPES.includes(key) ||
          ['_non_field_errors', '_error'].includes(key) ||
          key.includes('sectionFields') ||
          /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(key) ||
          /[A-Z]/.test(key.charAt(0))
        )
          return key;
        return convert(key, options);
      });
      return data;
    }
    return responseText;
  } catch (error: any) {
    const requestId = response.headers.get('x-request-id');
    const extra = {
      response: {
        url: response.url,
        status: response.status,
        statusText: response.statusText,
        responseText,
        ...(requestId && { 'x-request-id': requestId }),
      },
    };

    if (isOnServer) {
      console.error({
        url: response.url,
        status: response.status,
        status_text: response.statusText,
        response_text: responseText,
      });
    }
    captureExceptionWithRecording(error, extra);
    throw error;
  }
};

export const handleFormikFormError = (error: any) => ({
  message: error.message,
  isJSON: error.isJSON,
  ...error.errors,
  status: error.status,
});

// TODO: Translate all the following errors from AWS with a human-readable message
export const handleFormikAWSFormError = (error: any) => {
  const genericError = {
    message: 'There was a problem submitting the form.',
  };

  if (typeof error === 'string') {
    return {
      message: error,
    };
  }

  switch (error.name) {
    case 'UserNotConfirmedException':
    case 'UserNotFoundException':
    case 'UsernameExistsException':
    case 'InvalidEmailRoleAccessPolicyException':
    case 'UserLambdaValidationException':
    case 'AliasExistsException':
      return { ...genericError, email: error.message };

    case 'InvalidPasswordException':
      return { ...genericError, password: error.message };

    case 'ExpiredCodeException':
      return { ...genericError, code: error.message };

    default:
      return error;
  }
};

const api: IAPIHelper & APIProps = async (url = '', auth = false, config = { headers: undefined }) => {
  const { headers, SSRAuth, skipCamelization, ...init } = config;
  let newURL = url;
  let impersonatorToken = null;
  try {
    if (newURL.includes('language=') && !isOnServer) {
      const languageParam = /language=[^&]+/.exec(newURL)?.[0];
      const language = languageParam?.split('=')[1];
      const userCountry = getUserCountry();
      if (language === 'en' && userCountry.toLowerCase() === 'us') {
        newURL = newURL.replace(languageParam!, 'language=en-us');
      }
    }
    impersonatorToken = isOnServer ? null : sessionStorage.getItem(IMPERSONATOR_TOKEN);
  } catch {
    //  eslint-disable-next-line
    console.log('please enable cookies');
  }

  // XCMS CORS policy prevents X-CLIENT-TIMEZONE from being sent and the request fails.
  // This check prevents the header being sent on XCMS calls.
  const clientTimezone =
    !url.includes(CMS_API_HOST!) &&
    !url.includes(ZENDESK_API_URL!) &&
    !url.startsWith('https://cms.xcover.com/api/v2/xcover_website/frontend_messages/');

  const requestInit: RequestInit = {
    mode: 'cors',
    headers: await createHeaders(auth, headers, clientTimezone, impersonatorToken, SSRAuth),
    ...init,
  };

  // log for nextjs server request
  if (isOnServer) {
    const flattenObj = (obj: any, parent?: any, res: any = {}) => {
      for (const key of Object.keys(obj)) {
        const propName = parent ? `${parent}.${key}` : key;
        if (typeof obj[key] === 'object') {
          flattenObj(obj[key], propName, res);
        } else {
          res[propName] = obj[key];
        }
      }
      return res;
    };
    console.log(
      flattenObj({
        ...requestInit,
        url: newURL,
        headers: JSON.stringify(Object.fromEntries(requestInit.headers as Headers)),
      }),
    );
  }
  const response = await fetch(newURL, requestInit);
  const data = await processResponse(response, skipCamelization);
  if (!response.ok) {
    const requestId = response.headers.get('x-request-id');
    throw createError(response.status, data, newURL, requestId);
  }
  return data;
};

const get: IAPIHelper = (url, auth, config) => api(url, auth, { ...config, method: 'GET' });
const head: IAPIHelper = (url, auth, config) => api(url, auth, { ...config, method: 'HEAD' });
const post: IAPIHelper = (url, auth, config) => api(url, auth, { ...config, method: 'POST' });
const put: IAPIHelper = (url, auth, config) => api(url, auth, { ...config, method: 'PUT' });
const patch: IAPIHelper = (url, auth, config) => api(url, auth, { ...config, method: 'PATCH' });
const del: IAPIHelper = (url, auth, config) => api(url, auth, { ...config, method: 'DELETE' });

api.get = get;
api.head = head;
api.post = post;
api.put = put;
api.patch = patch;
api.del = del;

export default api;
